import React, { useState } from 'react';
import { View, Text, TouchableOpacity, FlatList, Modal, Alert, ActivityIndicator, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Linking from 'expo-linking';
import BenefitCard from '../BenefitCard';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { colors, fontFamily } from '../../constants/theme';

const GIFT_BROWN = '#5A403B';

function GiftFlowComponent({
    visible,
    onClose,
    benefits,
    membershipId,
    selectedRestaurant,
    firstTimeFlow = true
}) {
    const [selectedBenefit, setSelectedBenefit] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const giftPerkMutation = useMutation(api.referrals.giftPerk, {});
    // Web doesn't need safe area insets - use default values
    const insets = { top: 0, bottom: 0, left: 0, right: 0 };

    const handleClose = () => {
        setSelectedBenefit(null);
        onClose();
    };

    const handleSelectBenefit = (benefit) => {
        setSelectedBenefit(benefit);
    };

    const handleTextFriend = async () => {
        if (!selectedBenefit) {
            Alert.alert("Select a Perk", "Please select a perk to gift.");
            return;
        }

        setIsLoading(true);

        try {
            const result = await giftPerkMutation({
                membershipId: membershipId,
                benefitId: selectedBenefit._id,
            });

            if (result.success) {
                const referralCode = selectedBenefit._id;
                const appLink = `https://thedisgo.com/claim/${referralCode}`;
                const message = `Just dropped you a perk on DISGO! Claim it and check out one of my fave spots: ${appLink}`;
                
                // For web, we'll copy the link to clipboard instead of opening SMS
                if (navigator.clipboard) {
                    await navigator.clipboard.writeText(message);
                    Alert.alert(
                        "Link Copied!",
                        "The perk link has been copied to your clipboard. Share it with your friend!",
                        [
                            {
                                text: "OK",
                                onPress: () => {
                                    setSelectedBenefit(null);
                                }
                            }
                        ]
                    );
                } else {
                    // Fallback: show the link in an alert
                    Alert.alert(
                        "Share this link",
                        message,
                        [
                            {
                                text: "OK",
                                onPress: () => {
                                    setSelectedBenefit(null);
                                }
                            }
                        ]
                    );
                }
                
                Alert.alert(
                    "Your friend thanks you 🎁",
                    `+3 check-ins to ${selectedRestaurant.name} for you!`,
                    [
                        {
                            text: "OK",
                            onPress: () => {
                                setSelectedBenefit(null);
                            }
                        }
                    ]
                );
            } else {
                Alert.alert("Error", result.error || "Failed to gift perk.");
            }
        } catch (error) {
            console.error("Error gifting perk:", error);
            Alert.alert("Error", "An unexpected error occurred.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal visible={visible} animationType="slide" transparent={false}>
            <View style={styles.webRoot}>
                <View style={styles.main}>
                    <View style={[styles.backWrap, { top: insets.top + 16 }]}>
                        <TouchableOpacity onPress={handleClose}>
                            <Ionicons name="chevron-back" size={28} color={colors.brownDark} />
                        </TouchableOpacity>
                    </View>
                    <Text style={styles.title}>Gift a Perk!</Text>

                {benefits.length === 0 ? (
                    <Text style={styles.emptyMsg}>No Perks Available to Gift!</Text>
                ) : !selectedBenefit ? (
                    <>
                            <Text style={styles.selectPrompt}>Select a Perk to Gift:</Text>
                        <FlatList
                            style={styles.listRowWrap}
                            data={benefits}
                            keyExtractor={(item) => item._id}
                            contentContainerStyle={styles.listContent}
                            renderItem={({ item }) => (
                                <TouchableOpacity
                                    onPress={() => handleSelectBenefit(item)}
                                    style={styles.cardBtn}
                                >
                                    <BenefitCard benefit={item} />
                                </TouchableOpacity>
                            )}
                        />
                    </>
                ) : (
                    <View style={styles.confirmCol}>
                        <View style={styles.confirmCardWrap}>
                            <BenefitCard benefit={selectedBenefit} selected />
                        </View>
                        <Text style={styles.confirmHint}>
                                When they claim, you'll earn +3 points and get closer to the next tier.
                        </Text>

                        <TouchableOpacity
                            onPress={handleTextFriend}
                            style={styles.primaryBtn}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <ActivityIndicator color="#fff" />
                            ) : (
                                <View style={styles.primaryBtnInner}>
                                    <MaterialCommunityIcons name="message-text-outline" size={32} color="white" />
                                    <Text style={styles.primaryBtnText}>Text your friend</Text>
                                </View>
                            )}
                        </TouchableOpacity>
                    </View>
                )}
            </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
  webRoot: { flex: 1, backgroundColor: colors.white },
  main: { flex: 1, padding: 24, flexDirection: 'column', alignItems: 'center' },
  backWrap: { position: 'absolute', left: 32, zIndex: 40 },
  title: {
    fontSize: 36,
    fontFamily: fontFamily.abhayaBold,
    marginBottom: 16,
    marginTop: 48,
    color: colors.black,
  },
  emptyMsg: {
    fontSize: 20,
    color: GIFT_BROWN,
    marginBottom: 16,
    marginTop: 32,
    fontFamily: fontFamily.abhaya,
  },
  selectPrompt: {
    fontSize: 20,
    fontFamily: fontFamily.abhaya,
    color: GIFT_BROWN,
    marginVertical: 24,
  },
  listRowWrap: { flexDirection: 'row', flexWrap: 'wrap' },
  listContent: { alignItems: 'center' },
  cardBtn: { marginBottom: 12, borderRadius: 8, backgroundColor: colors.white },
  confirmCol: { alignItems: 'center', width: '100%' },
  confirmCardWrap: { marginVertical: 48 },
  confirmHint: { textAlign: 'center', fontSize: 18, marginBottom: 48, color: colors.black, fontFamily: fontFamily.abhaya },
  primaryBtn: { backgroundColor: GIFT_BROWN, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 24 },
  primaryBtnInner: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  primaryBtnText: { color: colors.white, fontSize: 24, fontFamily: fontFamily.abhayaBold, marginLeft: 8 },
});

export default GiftFlowComponent;
